<script lang="ts">
export default {
    name: 'BaseSwitcher',
};

export type BaseSwitcherValue = string | number;

export type BaseSwitcherOptionObject = {
    value: BaseSwitcherValue;
    label: string | number;
    disabled?: boolean;
    [key: string]: unknown;
};

export type BaseSwitcherOption = BaseSwitcherOptionObject | BaseSwitcherValue;

export type BaseSwitcherNormalizedOption = {
    value: string;
    label: string;
    disabled: boolean;
    checked: boolean;
    [key: string]: unknown;
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { DIRECTIONS } from '@/components/base-components/utils/direction';
import { Direction } from '@/components/base-components/types/direction';

const props = withDefaults(defineProps<{
    value?: BaseSwitcherValue;
    options: BaseSwitcherOption[];
    direction?: Direction;
    name?: string;
    disabled?: boolean;
    stretch?: boolean;
}>(), {
    direction: DIRECTIONS.HORIZONTAL,
    name: () => `base-switcher-${crypto.randomUUID()}`,
});

const emit = defineEmits<{
    (e: 'input', value: BaseSwitcherValue): void;
    (e: 'change', value: BaseSwitcherValue): void;
}>();

const list = computed<BaseSwitcherNormalizedOption[]>(() => props.options.map((option) => {
    const value = (typeof option === 'object' ? option.value : option).toString();
    const label = (typeof option === 'object' ? option.label : option).toString();

    return {
        ...(typeof option === 'object' ? option : {}),
        value,
        label,
        disabled: typeof option === 'object' ? option.disabled === true : false,
        checked: props.value?.toString?.() === value,
    };
}));

function onInput(value: BaseSwitcherValue) {
    if (props.disabled) return;

    emit('input', value);
    emit('change', value);
}
</script>

<template>
    <div class="relative flex">
        <div
            class="
                zi-0 flex bg-gray-200 rounded-md [--p:calc(var(--spacing)*0.5)]
                before:absolute before:zi-0 before:bg-gray-100/75 before:rounded-md before:transition-all before:duration-150 before:ease-in-out
                before:[position-anchor:--base-switcher-hover] before:[inset:calc(anchor(start)+var(--p))_calc(anchor(end)+var(--p))_calc(anchor(end)+var(--p))_calc(anchor(start)+var(--p))]
                after:absolute after:zi-0 after:bg-white after:rounded-sm after:transition-all after:duration-200 after:ease-in-out
                after:[position-anchor:--base-switcher-checked] after:[inset:calc(anchor(start)+var(--p))_calc(anchor(end)+var(--p))_calc(anchor(end)+var(--p))_calc(anchor(start)+var(--p))]
                has-[*:hover]:before:rounded-sm
            "
            :class="{
                'cursor-not-allowed pointer-events-none grayscale opacity-75': disabled,
                'flex-col': direction === DIRECTIONS.VERTICAL,
                'flex-1': stretch,
            }"
        >
            <label
                v-for="option in list"
                :key="option.value"
                class="
                    block relative zi-10 group p-0.5
                    [anchor-name:var(--base-switcher-anchor-hover,--a),var(--base-switcher-anchor-checked,--b)]
                    has-checked:[--base-switcher-anchor-checked:--base-switcher-checked]
                    not-has-disabled:hover:[--base-switcher-anchor-hover:--base-switcher-hover]
                "
                :class="{
                    '[&+&]:-ml-0.5': direction === DIRECTIONS.HORIZONTAL,
                    '[&+&]:-mt-0.5': direction === DIRECTIONS.VERTICAL,
                    'text-azure-800 grayscale-0': option.checked,
                    'text-gray-500 grayscale': !option.checked && !option.disabled,
                    'cursor-pointer hover:text-azure-800 hover:grayscale-0': !option.checked && !option.disabled,
                    'cursor-not-allowed text-gray-400': option.disabled,
                    'flex-1': stretch,
                }"
            >
                <input
                    class="sr-only absolute opacity-0 pointer-events-none"
                    type="radio"
                    :name="name"
                    :checked="option.checked"
                    :disabled="disabled || option.disabled"
                    @input="onInput(option.value)"
                >

                <slot name="option" :option="option" :checked="option.checked">
                    <span class="flex items-center justify-center font-semibold select-none rounded-sm px-3 py-1 gap-x-2">
                        <slot name="prefix" :option="option" :checked="option.checked" />

                        <slot name="label" :option="option" :checked="option.checked">
                            <span v-text="option.label" class="text-center text-sm" />
                        </slot>

                        <slot name="suffix" :option="option" :checked="option.checked" />
                    </span>
                </slot>
            </label>
        </div>
    </div>
</template>
